"use client";

import React from "react";
import { Factory, TrendingUp, Globe, Award } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  year: "2018",
  title: "BL Events Began",
  description: "Our journey began in Marion, SC as BL Events, where we specialized in delivering premium corporate and private experiences before transitioning our expertise into the digital landscape.",
  icon: Factory,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_sumter_origins.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_sumter_origins.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_sumter_origins.png-wvc-sizes",
    alt: "BlinkLab Media Sumter SC office"
  }
}, {
  year: "2020",
  title: "Digital Transformation",
  description: "As the world shifted, we evolved by expanding our service offerings and growing our specialized team to meet the rising demand for comprehensive digital solutions.",
  icon: TrendingUp,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_digital_growth.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_digital_growth.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_digital_growth.png-wvc-sizes",
    alt: "Digital marketing strategy session"
  }
}, {
  year: "2022",
  title: "Relocated to Sumter, SC & Launched BlinkLab Media",
  description: "We relocated our operations to Sumter, SC and officially introduced BlinkLab Media, a strategic evolution of BL Events designed to broaden our creative and technical capabilities.",
  icon: Globe,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_nationwide_expansion.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_nationwide_expansion.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_nationwide_expansion.png-wvc-sizes",
    alt: "Map showing nationwide client reach"
  }
}, {
  year: "2026",
  title: "Digital Media Services Nation Wide",
  description: "Today, we deliver advanced digital media solutions to businesses across the nation, establishing BlinkLab Media as a forward-thinking leader in the digital agency space.",
  icon: Award,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_future_vision.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_future_vision.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/blinklab_future_vision.png-wvc-sizes",
    alt: "Modern web design workspace"
  }
}];
function TimelineItem({
  milestone,
  index
}: {
  milestone: (typeof milestones)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    rootMargin: "0px 0px -50px 0px"
  });
  const isEven = index % 2 === 0;
  const Icon = milestone.icon;
  return <div data-index={index} ref={ref} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`absolute left-4 md:left-1/2 w-12 h-12 -ml-6 rounded-lg bg-background border-4 border-primary flex items-center justify-center z-20 top-0 md:top-1/2 md:-translate-y-1/2 ${index === 3 ? "md:-mt-12" : ""}`}>
        <Icon className="w-5 h-5 text-primary" />
      </div>

      {}
      <div className={`w-full md:w-5/12 pl-16 md:pl-0 ${isEven ? "md:pr-12 md:text-right" : "md:pl-12 md:ml-auto"}`}>
        <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-2xl overflow-hidden group hover:border-primary/50 transition-colors duration-500">
          <div className="relative h-48 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10" />
            <img src={milestone.image.src} data-wvc-srcset={milestone.image.srcset} data-wvc-sizes={milestone.image.sizes} alt={milestone.image.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className={`absolute bottom-4 z-20 ${isEven ? "md:right-6 left-6 md:left-auto" : "left-6"}`}>
              <span className="text-4xl font-bold font-default text-primary drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                {milestone.year}
              </span>
            </div>
          </div>
          <CardHeader className="relative z-20 pt-4">
            <CardTitle className="text-2xl font-default text-foreground">
              {milestone.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-20">
            <CardDescription className="text-base text-muted-foreground">
              {milestone.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block md:w-5/12" />
    </div>;
}
export default function OurStoryVision() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="265192" id="our-story-vision" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-secondary/20 via-background to-background pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/tech_grid_overlay.png')] opacity-5 mix-blend-overlay pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 text-foreground tracking-tight">
            Our Journey Since <span className="text-primary">2018</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            From our humble beginnings in Marion, South Carolina in 2018 as BL Events, to our expansion in Sumter and nationwide reach by 2026, we have grown into a trusted partner for businesses seeking digital transformation.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-2 md:w-4 -ml-1 md:-ml-2 bg-gradient-to-b from-secondary/30 via-primary/50 to-chart-3/30 rounded-lg border border-white/5 z-0">
            {}
            <div className="absolute inset-0 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687251/img/circuit_pattern_vertical.png')] opacity-20 mix-blend-overlay rounded-full" />
          </div>

          {}
          <div className="relative z-10 pt-8">
            {milestones.map((milestone, index) => <TimelineItem key={milestone.year} milestone={milestone} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}